import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { LocaleLink } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import { ArrowRight, CheckCircle, Star, Clock, Shield } from "lucide-react";

const BASE_URL = "https://www.craftfindus.com";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/** Build dynamic OG image URL for trade×city pages */
function buildOgImageUrl(trade: string, city: string, locale: string): string {
  return `${SUPABASE_URL}/functions/v1/og-image?trade=${encodeURIComponent(trade)}&city=${encodeURIComponent(city)}&locale=${encodeURIComponent(locale)}`;
}

interface LandingPageData {
  id: string;
  locale_prefix: string;
  slug: string;
  title: string;
  meta_description: string;
  h1: string;
  intro_text: string;
  how_it_works_text: string;
  cost_text: string;
  local_text: string;
  cta_text: string;
  faq_json: { question: string; answer: string }[];
  internal_links: { label: string; slug: string }[];
  trade_id: string;
  city_id: string;
  cluster_type: string;
  intent_type: string;
  cta_variant: string;
  seo_trades: { trade_label: string; trade_slug: string } | null;
  seo_cities: { city_name: string; city_slug: string; region: string } | null;
}

// Intent-based CTA mapping
const INTENT_CTAS: Record<string, { label: string; subtext: string }> = {
  informational: { label: "Kosten unverbindlich schätzen", subtext: "Erhalten Sie eine erste Einschätzung – kostenlos und unverbindlich." },
  commercial: { label: "Angebote vergleichen", subtext: "Vergleichen Sie bis zu 5 geprüfte Betriebe aus Ihrer Region." },
  transactional: { label: "Jetzt Auftrag starten", subtext: "Starten Sie noch heute – qualifizierte Handwerker warten auf Ihre Anfrage." },
};

// CTA variant labels for different cluster types
const CTA_VARIANTS: Record<string, { label: string; subtext: string }> = {
  standard: { label: "Jetzt Anfrage stellen", subtext: "Kostenlos und unverbindlich – 3 bis 5 geprüfte Betriebe." },
  cost_estimate: { label: "Kostenlose Kostenschätzung anfordern", subtext: "Erhalten Sie Preisvergleiche von geprüften Fachbetrieben." },
  emergency: { label: "Notdienst jetzt anfordern", subtext: "Schnelle Hilfe – auch nachts und am Wochenende." },
  comparison: { label: "Betriebe vergleichen", subtext: "Finden Sie den besten Anbieter für Ihr Projekt." },
  funding: { label: "Förderfähige Betriebe finden", subtext: "Unsere Partner kennen die aktuellen Förderprogramme." },
  guide: { label: "Projekt starten", subtext: "Setzen Sie Ihre Tipps direkt um – mit geprüften Betrieben." },
};

export default function SEOLandingPage() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t } = useTranslation();
  const [page, setPage] = useState<LandingPageData | null>(null);
  const [relatedCities, setRelatedCities] = useState<any[]>([]);
  const [relatedTrades, setRelatedTrades] = useState<any[]>([]);
  const [relatedBlogs, setRelatedBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !locale) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("seo_landing_pages")
        .select("*, seo_trades(trade_label, trade_slug), seo_cities(city_name, city_slug, region)")
        .eq("locale_prefix", locale)
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (data) {
        setPage(data as any);

        // Parallel: related cities (same trade), related trades (same city), related blogs
        if (data.trade_id) {
          const { data: d } = await supabase
            .from("seo_landing_pages")
            .select("slug, h1, seo_cities(city_name)")
            .eq("locale_prefix", locale)
            .eq("trade_id", data.trade_id)
            .eq("is_published", true)
            .neq("slug", slug)
            .limit(8);
          setRelatedCities(d || []);
        }

        if (data.city_id) {
          const { data: d } = await supabase
            .from("seo_landing_pages")
            .select("slug, h1, seo_trades(trade_label)")
            .eq("locale_prefix", locale)
            .eq("city_id", data.city_id)
            .eq("is_published", true)
            .neq("slug", slug)
            .limit(8);
          setRelatedTrades(d || []);
        }

        if (data.trade_id) {
          const { data: d } = await supabase
            .from("blog_posts")
            .select("slug, title")
            .eq("locale_prefix", locale)
            .eq("is_published", true)
            .eq("related_trade_id", data.trade_id)
            .limit(3);
          setRelatedBlogs(d || []);
        }
      }
      setLoading(false);
    })();
  }, [slug, locale]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background flex items-center justify-center">
          <p className="text-caption">{t("common.loading")}</p>
        </main>
      </>
    );
  }

  if (!page) return null;

  const tradeName = page.seo_trades?.trade_label || "";
  const cityName = page.seo_cities?.city_name || "";
  const regionName = page.seo_cities?.region || "";
  const faq = Array.isArray(page.faq_json) ? page.faq_json : [];
  const intentCta = INTENT_CTAS[page.intent_type || "informational"] || INTENT_CTAS.informational;
  const ctaVariant = CTA_VARIANTS[page.cta_variant || "standard"] || CTA_VARIANTS.standard;

  const breadcrumbs = [
    { name: "CRAFTFINDUS", url: `${BASE_URL}/${locale}/` },
    ...(tradeName ? [{ name: tradeName, url: `${BASE_URL}/${locale}/${page.seo_trades?.trade_slug}/` }] : []),
    { name: page.h1, url: `${BASE_URL}/${locale}/${slug}/` },
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${tradeName} in ${cityName}`,
    description: page.meta_description,
    areaServed: { "@type": "City", name: cityName, containedInPlace: { "@type": "AdministrativeArea", name: regionName } },
    provider: { "@type": "Organization", name: "CRAFTFINDUS", url: BASE_URL },
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: t("seoLanding.freeForClients") },
  };

  const faqSchema = faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  } : null;

  return (
    <>
      <Header />
      <main id="main-content" className="bg-background">
        <SEOHead
          title={page.title}
          description={page.meta_description || ""}
          canonical={`/${slug}`}
          image={tradeName && cityName ? buildOgImageUrl(tradeName, cityName, locale || "de") : undefined}
        />
        <JsonLd breadcrumbs={breadcrumbs} customSchemas={[serviceSchema, ...(faqSchema ? [faqSchema] : [])]} />

        {/* Hero Section */}
        <section className="bg-primary/5 py-16 md:py-24">
          <div className="container max-w-4xl mx-auto px-4">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                {breadcrumbs.map((bc, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    {i > 0 && <span>/</span>}
                    {i < breadcrumbs.length - 1 ? (
                      <LocaleLink to={bc.url.replace(`${BASE_URL}/${locale}`, "")} className="hover:text-foreground transition-colors">
                        {bc.name}
                      </LocaleLink>
                    ) : (
                      <span className="text-foreground">{bc.name}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">{page.h1}</h1>
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><CheckCircle className="w-4 h-4 text-primary" />{t("seoLanding.verified")}</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><Shield className="w-4 h-4 text-primary" />{t("seoLanding.free")}</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><Clock className="w-4 h-4 text-primary" />{t("seoLanding.fast")}</span>
            </div>
            <LocaleLink to="/inquiry">
              <Button variant="commit" size="lg">
                {intentCta.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </LocaleLink>
            <p className="text-sm text-muted-foreground mt-3">{intentCta.subtext}</p>
          </div>
        </section>

        {/* What is CRAFTFINDUS - AI Search Optimization */}
        <section className="py-12 md:py-16 border-b border-border">
          <div className="container max-w-4xl mx-auto px-4">
            <h2 className="text-xl font-semibold mb-4">{t("seoLanding.whatIsCraftfindus")}</h2>
            <p className="text-body text-muted-foreground leading-relaxed">
              {t("seoLanding.whatIsCraftfindusText")}
            </p>
          </div>
        </section>

        {/* Intro */}
        {page.intro_text && (
          <section className="py-12 md:py-16 border-b border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-4">{t("seoLanding.verifiedProviders", { trade: tradeName, city: cityName })}</h2>
              <div className="text-body text-muted-foreground leading-relaxed whitespace-pre-line">{page.intro_text}</div>
            </div>
          </section>
        )}

        {/* How it works */}
        {page.how_it_works_text && (
          <section className="py-12 md:py-16 bg-secondary/20 border-b border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-4">{t("seoLanding.howItWorks")}</h2>
              <div className="text-body text-muted-foreground leading-relaxed whitespace-pre-line">{page.how_it_works_text}</div>
            </div>
          </section>
        )}

        {/* Costs */}
        {page.cost_text && (
          <section className="py-12 md:py-16 border-b border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-4">{t("seoLanding.costs", { trade: tradeName })}</h2>
              <div className="text-body text-muted-foreground leading-relaxed whitespace-pre-line">{page.cost_text}</div>
            </div>
          </section>
        )}

        {/* Local text */}
        {page.local_text && (
          <section className="py-12 md:py-16 border-b border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-4">{tradeName} {t("seoLanding.inCity", { city: cityName })}</h2>
              <div className="text-body text-muted-foreground leading-relaxed whitespace-pre-line">{page.local_text}</div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="py-12 md:py-16 bg-secondary/20 border-b border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-6">{t("seoLanding.faq")}</h2>
              <div className="space-y-6">
                {faq.map((item, i) => (
                  <details key={i} className="group bg-card border border-border rounded-sm p-4">
                    <summary className="cursor-pointer font-medium text-foreground flex items-center justify-between">
                      {item.question}
                      <Star className="w-4 h-4 text-muted-foreground group-open:rotate-45 transition-transform" />
                    </summary>
                    <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Intent-based CTA */}
        <section className="py-16 md:py-24 bg-primary/5">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">{page.cta_text || t("seoLanding.ctaHeading", { trade: tradeName, city: cityName })}</h2>
            <p className="text-muted-foreground mb-8">{ctaVariant.subtext}</p>
            <LocaleLink to="/inquiry">
              <Button variant="commit" size="lg">
                {ctaVariant.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </LocaleLink>
          </div>
        </section>

        {/* Related trades in same city - Internal linking */}
        {relatedTrades.length > 0 && (
          <section className="py-12 md:py-16 border-t border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-6">{t("seoLanding.otherTrades", { city: cityName })}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {relatedTrades.map((rp: any) => (
                  <LocaleLink key={rp.slug} to={`/${rp.slug}`} className="p-3 border border-border rounded-sm hover:bg-secondary/30 transition-colors text-sm">
                    {rp.seo_trades?.trade_label || rp.h1}
                  </LocaleLink>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related cities for same trade - Internal linking */}
        {relatedCities.length > 0 && (
          <section className="py-12 md:py-16 border-t border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-6">{t("seoLanding.relatedCities", { trade: tradeName })}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {relatedCities.map((rp: any) => (
                  <LocaleLink key={rp.slug} to={`/${rp.slug}`} className="p-3 border border-border rounded-sm hover:bg-secondary/30 transition-colors text-sm">
                    {rp.seo_cities?.city_name || rp.h1}
                  </LocaleLink>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related blog posts */}
        {relatedBlogs.length > 0 && (
          <section className="py-12 md:py-16 border-t border-border">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-xl font-semibold mb-6">{t("seoLanding.relatedArticles")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedBlogs.map((bp: any) => (
                  <LocaleLink key={bp.slug} to={`/blog/${bp.slug}`} className="p-4 border border-border rounded-sm hover:bg-secondary/30 transition-colors">
                    <p className="text-sm font-medium">{bp.title}</p>
                  </LocaleLink>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
